<template>
    <div class="trigger-row mb-2" :class="{ 'has-error': hasRowError }">
        <input v-if="localTrigger.id" type="hidden" :name="fieldName('id')" :value="localTrigger.id">

        <div class="row trigger-row-grid">
            <div class="col-md-12">
                <div class="row">
                    <div class="col-md-4 form-group" :class="{ 'has-error': fieldError('condition_value') }">
                        <template v-if="isEditing && !disabled">
                            <product-gift-picker
                                v-model="localTrigger.condition_value"
                                v-model:product-id="localTrigger.trigger_product_id"
                                v-model:primary-artist="localTrigger.trigger_product_primary_artist"
                                v-model:title="localTrigger.trigger_product_title"
                                :label="labels.ean + '*'"
                                :placeholder="labels.searchProductPlaceholder"
                                :search-url="searchUrl"
                                :input-id="`trigger-product-picker-${index}`"
                                :ean-name="fieldName('condition_value')"
                                :product-id-name="fieldName('trigger_product_id')"
                                :error-message="fieldError('condition_value') || eanError"
                            ></product-gift-picker>
                        </template>
                        <template v-else>
                            <label class="control-label">{{ labels.ean }}</label>
                            <p class="form-control-static trigger-row-view">{{ localTrigger.condition_value || '—' }}</p>
                            <template v-if="!disabled">
                                <input type="hidden" :name="fieldName('condition_value')" :value="localTrigger.condition_value">
                                <input type="hidden" :name="fieldName('trigger_product_id')" :value="localTrigger.trigger_product_id || ''">
                                <input type="hidden" :name="fieldName('trigger_product_primary_artist')" :value="localTrigger.trigger_product_primary_artist || ''">
                                <input type="hidden" :name="fieldName('trigger_product_title')" :value="localTrigger.trigger_product_title || ''">
                            </template>
                        </template>
                    </div>

                    <div class="col-md-4 form-group">
                        <template v-if="isEditing && !disabled">
                            <label class="control-label">{{ labels.primaryArtist }}</label>
                            <input
                                v-model="localTrigger.trigger_product_primary_artist"
                                class="form-control"
                                type="text"
                                :name="fieldName('trigger_product_primary_artist')"
                                readonly
                            >
                        </template>
                        <template v-else>
                            <label class="control-label">{{ labels.primaryArtist }}</label>
                            <p class="form-control-static trigger-row-view">{{ localTrigger.trigger_product_primary_artist || '—' }}</p>
                        </template>
                    </div>

                    <div class="col-md-4 form-group">
                        <template v-if="isEditing && !disabled">
                            <label class="control-label">{{ labels.productTitle }}</label>
                            <input
                                v-model="localTrigger.trigger_product_title"
                                class="form-control"
                                type="text"
                                :name="fieldName('trigger_product_title')"
                                readonly
                            >
                        </template>
                        <template v-else>
                            <label class="control-label">{{ labels.productTitle }}</label>
                            <p class="form-control-static trigger-row-view">{{ localTrigger.trigger_product_title || '—' }}</p>
                        </template>
                    </div>
                </div>
            </div>

            <div class="col-md-2 form-group" :class="{ 'has-error': fieldError('gift_quantity') }">
                <template v-if="isEditing && !disabled">
                    <label class="control-label">{{ labels.giftQuantity }}</label>
                    <input
                        v-model.number="localTrigger.gift_quantity"
                        class="form-control"
                        type="number"
                        min="1"
                        :name="fieldName('gift_quantity')"
                    >
                    <p v-if="fieldError('gift_quantity')" class="help-block">{{ fieldError('gift_quantity') }}</p>
                </template>
                <template v-else>
                    <label class="control-label">{{ labels.giftQuantity }}</label>
                    <p class="form-control-static trigger-row-view">{{ localTrigger.gift_quantity }}</p>
                    <input v-if="!disabled" type="hidden" :name="fieldName('gift_quantity')" :value="localTrigger.gift_quantity">
                </template>
            </div>

            <div class="col-md-2 form-group" :class="{ 'has-error': fieldError('active') }">
                <slider-button
                    v-model="localTrigger.active"
                    :label="labels.active"
                    :name="disabled ? '' : fieldName('active')"
                    :input-id="`trigger-active-${index}`"
                    label-align="vertical"
                    :disabled="disabled"
                ></slider-button>
                <p v-if="fieldError('active')" class="help-block">{{ fieldError('active') }}</p>
            </div>

            <div v-if="!disabled" class="col-md-8 form-group">
                <label class="control-label">&nbsp;</label>
                <div class="trigger-row-actions">
                    <template v-if="isEditing">
                        <template v-if="isEditCampaign">
                            <button type="button" class="btn btn-success btn-sm" @click="save">{{ labels.save }}</button>
                            <button type="button" class="btn btn-default btn-sm trigger-row-btn-spaced" @click="cancel">{{ labels.cancel }}</button>
                        </template>
                        <button
                            v-else-if="isCreateCampaign"
                            type="button"
                            class="btn btn-default btn-sm"
                            @click="cancelCreateRow"
                        >{{ labels.cancel }}</button>
                    </template>
                    <template v-else>
                        <button
                            v-if="isEditCampaign"
                            type="button"
                            class="btn btn-primary btn-sm"
                            @click="startEdit"
                        >{{ labels.edit }}</button>
                        <button type="button" class="btn btn-danger btn-sm" :class="{ 'trigger-row-btn-spaced': isEditCampaign }" @click="confirmDelete">{{ labels.delete }}</button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SliderButton from '@admin-partials/SliderButton.vue';
import ProductGiftPicker from '../partials/ProductGiftPicker.vue';

export default {
    components: {
        SliderButton,
        ProductGiftPicker,
    },
    props: {
        trigger: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        activeOptions: {
            type: Object,
            required: true,
        },
        labels: {
            type: Object,
            required: true,
        },
        searchUrl: {
            type: String,
            required: true,
        },
        validationErrors: {
            type: Object,
            default: () => ({}),
        },
        startInEditMode: {
            type: Boolean,
            default: false,
        },
        campaignMode: {
            type: String,
            default: 'create',
            validator: (value) => ['create', 'edit', 'show'].includes(value),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update', 'delete'],
    data() {
        return {
            isEditing: false,
            localTrigger: { ...this.trigger },
            snapshot: null,
            eanError: null,
        };
    },
    computed: {
        hasRowError() {
            return Boolean(
                this.fieldError('condition_value')
                || this.fieldError('gift_quantity')
                || this.fieldError('active'),
            );
        },
        isCreateCampaign() {
            return this.campaignMode === 'create';
        },
        isEditCampaign() {
            return this.campaignMode === 'edit';
        },
    },
    mounted() {
        if (this.disabled || this.campaignMode === 'show') {
            this.isEditing = false;
            return;
        }

        if (this.isCreateCampaign) {
            this.isEditing = true;
            return;
        }

        if (this.startInEditMode || (!this.localTrigger.id && !this.localTrigger.condition_value)) {
            this.isEditing = true;
        }
    },
    methods: {
        fieldName(suffix) {
            return `triggers[${this.index}][${suffix}]`;
        },
        fieldError(field) {
            const key = `triggers.${this.index}.${field}`;
            const messages = this.validationErrors[key];

            return Array.isArray(messages) ? messages[0] : null;
        },
        startEdit() {
            this.snapshot = { ...this.localTrigger };
            this.eanError = null;
            this.isEditing = true;
        },
        save() {
            const ean = String(this.localTrigger.condition_value || '').trim();
            if (ean === '') {
                this.eanError = this.labels.eanRequired;
                return;
            }

            this.localTrigger.condition_value = ean;
            this.localTrigger.gift_quantity = Math.max(1, parseInt(this.localTrigger.gift_quantity, 10) || 1);
            this.localTrigger.active = Boolean(this.localTrigger.active);
            this.eanError = null;
            if (this.isEditCampaign) {
                this.isEditing = false;
            }
            this.$emit('update', { ...this.localTrigger });
        },
        cancel() {
            if (!this.localTrigger.id && !this.snapshot) {
                this.$emit('delete');
                return;
            }

            if (this.snapshot) {
                this.localTrigger = { ...this.snapshot };
            }

            this.eanError = null;
            this.isEditing = false;
            this.$emit('update', { ...this.localTrigger });
        },
        cancelCreateRow() {
            this.$emit('delete');
        },
        confirmDelete() {
            Swal.fire({
                text: this.labels.deleteTriggerConfirm,
                icon: 'warning',
                buttonsStyling: false,
                showCancelButton: true,
                cancelButtonText: this.labels.cancel,
                confirmButtonText: this.labels.delete,
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-default',
                },
            }).then((result) => {
                if (result.value) {
                    this.$emit('delete');
                }
            });
        },
    },
};
</script>

<style scoped>
.trigger-row-btn-spaced {
    margin-left: 8px;
}

.trigger-row-view {
    padding-top: 7px;
    margin-bottom: 0;
}

.trigger-row-actions {
    padding-top: 7px;
}
</style>
